<template>
  <div class="save-form">
    <div class="save-field">
      <label class="save-label" for="saveQueryName">Name <span aria-hidden="true">*</span></label>
      <input
        id="saveQueryName"
        class="save-input"
        type="text"
        maxlength="256"
        autocomplete="off"
        :disabled="disabled"
        :aria-invalid="nameError ? 'true' : 'false'"
        aria-describedby="saveNameHint"
        v-model="formObject.name"
      >
      <div id="saveNameHint" class="save-hint">Choose a clear name; including analytes, locations, or dates can help identify the query.</div>
      <div class="save-counter"><span>{{ nameLength }}</span> / 256</div>
      <div v-if="nameError" class="save-error">{{ nameError }}</div>
    </div>

    <div class="save-field">
      <label class="save-label" for="saveQueryDescription">Description</label>
      <textarea
        id="saveQueryDescription"
        class="save-input save-textarea"
        maxlength="512"
        :disabled="disabled"
        aria-describedby="saveDescriptionHint"
        v-model="formObject.description"
      ></textarea>
      <div id="saveDescriptionHint" class="save-hint">Describe the purpose of this query.</div>
      <div class="save-counter"><span>{{ descriptionLength }}</span> / 512</div>
      <div v-if="descriptionError" class="save-error">{{ descriptionError }}</div>
    </div>

    <div class="save-field">
      <label class="save-toggle" for="saveRelativeDates">
        <input id="saveRelativeDates" type="checkbox" :disabled="disabled" v-model="hasRelativeDates">
        <v-icon small>date_range</v-icon>
        <span>Relative dates</span>
      </label>
      <div class="save-hint">
        <template v-if="hasRelativeDates">Date range is saved <strong>relative to the view date</strong>. Uncheck this to save exact dates.</template>
        <template v-else>Date range is saved <strong>exactly as defined</strong>. Enable this to save it relative to the view date.</template>
      </div>
    </div>

    <div class="save-field">
      <label class="save-toggle" for="saveShared">
        <input id="saveShared" type="checkbox" :disabled="disabled" v-model="formObject.isShared">
        <v-icon small>share</v-icon>
        <span>Share with others</span>
      </label>
      <div class="save-hint">{{ formObject.isShared ? 'Users with access to the data can use this query, but cannot edit it.' : 'Only you can use this query.' }}</div>
    </div>

    <section v-if="isSomeAdmin" class="save-admin" aria-labelledby="saveAdminTitle">
      <h3 id="saveAdminTitle" class="save-section-title">Administration <span class="save-admin-note">Admin only</span></h3>
      <div class="save-field">
        <label class="save-label" for="saveQueryUser">User</label>
        <select id="saveQueryUser" class="save-input" :disabled="disabled" aria-describedby="saveUserHint" v-model="formObject.userId">
          <option :value="null">Assign to yourself</option>
          <option v-for="user in users" :key="user.UserId" :value="user.UserId">{{ user.UserName }}</option>
        </select>
        <div id="saveUserHint" class="save-hint">Assign this query to another user, or leave empty to assign it to yourself.</div>
        <div v-if="userError" class="save-error">{{ userError }}</div>
      </div>
    </section>
  </div>
</template>
<script>
  import moment from 'moment'

  export default {
    props: {
      disabled: Boolean,
    },
    data() {
      return {
        formObject: {},
        initialObject: {
          id: null,
          name: '',
          userId: null,
          description: '',
          isShared: false,
          relativeDate: null,
        },
        submitted: false,
        errorMessages: {
          name: [],
          userId: [],
          description: [],
        },
      }
    },
    computed: {
      nameLength() {
        return (this.formObject.name || '').length
      },
      descriptionLength() {
        return (this.formObject.description || '').length
      },
      hasServerErrors() {
        return ['name', 'description', 'userId'].some(key => (this.errorMessages[key] || []).length > 0)
      },
      nameError() {
        if (this.errorMessages.name && this.errorMessages.name.length) return this.errorMessages.name[0]
        if (!this.formObject.name && this.submitted) return 'Name is required.'
        if (this.nameLength > 256) return 'Name must be 256 characters or fewer'
        return ''
      },
      descriptionError() {
        if (this.errorMessages.description && this.errorMessages.description.length) return this.errorMessages.description[0]
        if (this.descriptionLength > 512) return 'Description must be 512 characters or fewer'
        return ''
      },
      userError() {
        if (this.errorMessages.userId && this.errorMessages.userId.length) return this.errorMessages.userId[0]
        return ''
      },
      isValid() {
        return !this.hasServerErrors &&
          !!String(this.formObject.name || '').trim() &&
          this.nameLength <= 256 &&
          this.descriptionLength <= 512
      },
      hasRelativeDates: {
        get() {
          return !!this.formObject.relativeDate
        },
        set(value) {
          if (value) {
            this.formObject.relativeDate = moment().toDate()
          } else {
            this.formObject.relativeDate = null
          }
        },
      },
      users() {
        return this.$store.getters['admin/userList']
      },
      isSomeAdmin() {
        return this.$store.getters['authenticate/isSomeAdmin']
      },
      username() {
        return this.$store.getters['authenticate/username']
      },
      currentUser() {
        return this.$store.getters['authenticate/currentUser']
      },
      userId() {
        return this.currentUser && this.currentUser.userId
      },
      queries() {
        return this.$store.getters['serviceconfigurations/configurations']
      },
      selectedUserQueries() {
        let userId = this.formObject.userId || this.userId
        return this.queries.filter(query => query.userId == userId)
      },
      selectedUser() {
        if (!this.formObject.userId) return
        return this.users.find(user => user.UserId == this.formObject.userId)
      },
    },
    watch: {
      selectedUser: {
        immediate: true,
        deep: true,
        handler(value) {
          this.$emit('update:selectedUser', value || this.currentUser)
        },
      },
      isValid(value) {
        this.$emit('update:isValid', value)
      },
      formObject(value) {
        this.$emit('update:formData', value)
      },
    },
    methods: {
      setErrorMessages(errorMessages) {
        this.errorMessages = errorMessages
      },
      clearErrorMessages() {
        this.errorMessages = {
          name: [],
          userId: [],
          description: [],
        }
      },
      validate() {
        this.clearErrorMessages()
        this.submitted = true
        this.$emit('update:isValid', this.isValid)
      },
      setFormObject(data) {
        this.formObject = JSON.parse(JSON.stringify(data))
      },
      setQueryDetails(queryDetails) {
        const defaultUserId = this.currentUser && this.currentUser.userId
        this.formObject = {
          ...this.initialObject,
          userId: defaultUserId || null,
          ...queryDetails,
        }
      },
    },
    created() {
      this.setQueryDetails({})
    },
  }
</script>

<style scoped>
  /* Field chrome mirrors the mockup's `.save-field` / `.save-hint` / `.save-counter`. */
  .save-field {
    margin: 0 0 16px;
  }

  .save-label {
    display: block;
    margin-bottom: 5px;
    color: #444;
    font-size: 13px;
    font-weight: 500;
  }

  .save-input {
    display: block;
    width: 100%;
    min-height: 40px;
    padding: 8px 10px;
    border: 1px solid rgba(0, 0, 0, .28);
    border-radius: 4px;
    background: #fff;
    color: #333;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.4;
  }

  .save-input:focus {
    outline: none;
    border-color: #0b82ea;
    box-shadow: 0 0 0 2px rgba(11, 130, 234, .18);
  }

  .save-input[aria-invalid="true"] {
    border-color: #b9121b;
  }

  .save-input:disabled {
    background: #f3f3f3;
    color: #666;
  }

  .save-textarea {
    min-height: 88px;
    resize: vertical;
  }

  .save-hint {
    margin-top: 4px;
    color: #666;
    font-size: 12px;
  }

  .save-counter {
    margin-top: 2px;
    color: #666;
    font-size: 11px;
    text-align: right;
  }

  .save-error {
    margin-top: 5px;
    color: #84252b;
    font-size: 12px;
  }

  .save-toggle {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    margin: 14px 0 5px;
    color: #333;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
  }

  .save-toggle input {
    width: 16px;
    height: 16px;
    flex: none;
    margin: 3px 0 0;
    accent-color: #529943;
  }

  .save-toggle .v-icon {
    width: 17px;
    height: 17px;
    color: #555;
    font-size: 17px !important;
  }

  .save-section-title {
    margin: 20px 0 8px;
    padding-bottom: 6px;
    border-bottom: 1px solid rgba(0, 0, 0, .14);
    color: #333;
    font-size: 13px;
    font-weight: 600;
  }

  .save-admin-note {
    margin-left: 5px;
    color: #666;
    font-size: 11px;
    font-weight: 400;
  }
</style>